import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiX, FiFolder, FiFile, FiArrowRight } from "react-icons/fi";
import { FaTags } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import api from "../services/api";
import { tools } from "../utils/tools";
import { isAdminUser } from "../utils/roleUtils";
import "./GlobalSearch.css";

const GlobalSearch = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({ tools: [], folders: [], files: [], vendors: [] });
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allVendors, setAllVendors] = useState([]);
    const searchRef = useRef(null);

    // Pre-load vendors once on mount for instant client-side search
    useEffect(() => {
        api.get("/vendors").then(res => setAllVendors(res.data || [])).catch(() => { });
    }, []);

    // Close dropdown on outside click or Esc key
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    // Search Logic with Debounce
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (query.trim().length > 1) {
                setLoading(true);
                try {
                    // 1. Search Tools (Client-side)
                    const matchedTools = tools.filter(t =>
                        t.name.toLowerCase().includes(query.toLowerCase()) ||
                        t.description.toLowerCase().includes(query.toLowerCase())
                    );

                    // 2. Search Folders (API)
                    const folderRes = await api.get(`/folders/search?query=${query}`);

                    // 3. Search Files (API) - New Endpoint
                    const fileRes = await api.get(`/folders/files/search?query=${query}`);

                    const matchedVendors = allVendors.filter(v =>
                        v.vendorName.toLowerCase().includes(query.toLowerCase()) ||
                        (v.instructions && v.instructions.toLowerCase().includes(query.toLowerCase()))
                    );

                    setResults({
                        tools: matchedTools,
                        folders: folderRes.data || [],
                        files: fileRes.data || [],
                        vendors: matchedVendors,
                    });
                    setShowDropdown(true);
                } catch (err) {
                    console.error("Search failed", err);
                } finally {
                    setLoading(false);
                }
            } else {
                setResults({ tools: [], folders: [], files: [], vendors: [] });
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [query, allVendors]);

    const handleNavigate = (type, item) => {
        setQuery("");
        setShowDropdown(false);

        if (type === "tool") {
            navigate(item.path);
        } else if (type === "vendor") {
            navigate(`/purchase-programs?vendorId=${item.id}`);
        } else if (type === "folder") {
            const targetPath = isAdminUser(user) ? "/admin" : "/employee";
            navigate(`${targetPath}?tab=files&folderId=${item.id}`);
        } else if (type === "file") {
            // Navigate to folder containing the file
            const targetPath = isAdminUser(user) ? "/admin" : "/employee";
            navigate(`${targetPath}?tab=files&folderId=${item.folderId}&highlightFile=${item.id}`);
        }
    };

    const hasResults = results.tools.length > 0 || results.folders.length > 0 || results.files.length > 0 || results.vendors.length > 0;

    return (
        <div className="global-search-container" ref={searchRef}>
            <div className="search-input-wrapper">
                <input
                    type="text"
                    placeholder="Search tools, folders, files..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => { if (query.length > 1) setShowDropdown(true); }}
                />
                <FiSearch className="search-icon-main" />
                {query && (
                    <button className="search-clear-btn" onClick={() => setQuery("")}>
                        <FiX />
                    </button>
                )}
            </div>

            {showDropdown && (
                <div className="global-search-dropdown">
                    {!hasResults && !loading && (
                        <div className="search-section" style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
                            No results found for "{query}"
                        </div>
                    )}

                    {loading && (
                        <div className="search-section" style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
                            Searching...
                        </div>
                    )}

                    {/* Tools Section */}
                    {results.tools.length > 0 && (
                        <div className="search-section">
                            <div className="section-header">Tools</div>
                            {results.tools.map(tool => (
                                <div key={tool.id} className="result-item tool" onClick={() => handleNavigate("tool", tool)}>
                                    <div className="item-icon"><tool.icon /></div>
                                    <div className="item-content">
                                        <div className="item-title">{tool.name}</div>
                                        <div className="item-desc">{tool.description}</div>
                                    </div>
                                    <FiArrowRight style={{ opacity: 0.5 }} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Folders Section */}
                    {results.folders.length > 0 && (
                        <div className="search-section">
                            <div className="section-header">Folders</div>
                            {results.folders.map(folder => (
                                <div key={folder.id} className="result-item folder" onClick={() => handleNavigate("folder", folder)}>
                                    <div className="item-icon"><FiFolder /></div>
                                    <div className="item-content">
                                        <div className="item-title">{folder.name}</div>
                                        <div className="item-desc">In {folder.ancestors && folder.ancestors.length > 0 ? folder.ancestors[folder.ancestors.length - 1].name : "Root"}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Purchase Programs Section */}
                    {results.vendors.length > 0 && (
                        <div className="search-section">
                            <div className="section-header">Purchase Programs</div>
                            {results.vendors.slice(0, 5).map(vendor => (
                                <div key={vendor.id} className="result-item tool" onClick={() => handleNavigate("vendor", vendor)}>
                                    <div className="item-icon"><FaTags /></div>
                                    <div className="item-content">
                                        <div className="item-title">{vendor.vendorName}</div>
                                        <div className="item-desc">Employee Purchase Program</div>
                                    </div>
                                    <FiArrowRight style={{ opacity: 0.5 }} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Files Section */}
                    {results.files.length > 0 && (
                        <div className="search-section">
                            <div className="section-header">Files</div>
                            {results.files.map(file => (
                                <div key={file.id} className="result-item file" onClick={() => handleNavigate("file", file)}>
                                    <div className="item-icon"><FiFile /></div>
                                    <div className="item-content">
                                        <div className="item-title">{file.name}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="search-footer">
                        <span className="shortcut-hint">
                            Search for anything across the platform
                        </span>
                        <div className="shortcut-hint">
                            <span className="key">ESC</span> to close
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalSearch;
